import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import Paper from '@mui/material/Paper';

import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import { useTranslation } from 'react-i18next';

import TableRow from '@mui/material/TableRow';

import addCommas from '../../utils/functions/commas';

import { styled } from '@mui/material/styles';
import dayjs from 'dayjs';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

function TablePendingDetail({ invoice, transaction, uploadRef }) {

  const { t } = useTranslation();

  const columns = [
    { id: 'uploadRef', label: t('app.uploadRef'), minWidth: 100 },

    {
      id: 'transaction_ref',
      label: t('app.transactionRefNo'),
      minWidth: 100,
      align: 'center',
    },
    {
      id: 'customer_code',
      label: t('navbar.custNo'),
      minWidth: 100,
      align: 'center',
    },
    {
      id: 'invoice_no',
      label: t('app.invoiceNo'),
      minWidth: 100,
      align: 'center',
    },
    {
      id: 'wht_rate',
      label: t('app.whtPercent'),
      minWidth: 100,
      align: 'center',
    },
    {
      id: 'wht_amount',
      label: t('app.whtThb'),
      minWidth: 100,
      align: 'center',
    },
    {
      id: 'payment_date',
      label: t('app.paymentDate'),
      minWidth: 100,
      align: 'center',
    },
    { id: 'fee', label: t('app.feeThb'), minWidth: 100, align: 'center' },
    {
      id: 'price_gl_diff',
      label: t('app.roundingAmount'),
      minWidth: 100,
      align: 'center',
    },
    {
      id: 'add_gl_diff',
      label: t('app.roundingCondition'),
      minWidth: 100,
      align: 'center',
    },
    {
      id: 'amount',
      label: t('app.amountExcludeVat'),
      maxWidth: 100,
      align: 'center',
    },
    {
      id: 'pay_amount',
      label: t('app.grandTotal'),
      minWidth: 100,
      align: 'center',
    },
  ];

  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);

  const merchant = useSelector((state) => state.merchant.value);

  const [dataFiltered, setDataFiltered] = useState([]);
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const displayedData = dataFiltered.slice(startIndex, endIndex);
  


  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

   const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0); // เริ่มที่หน้า 0 เมื่อเปลี่ยนจำนวนแถวต่อหน้า
  };

  useEffect(() => {
    setDataFiltered(invoice || []);
  }, [invoice]);

  return (
    <div className="mt-5">
      <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      <TableContainer sx={{ overflowX: 'auto', overflowY: 'visible' }}>

          <Table size="small">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <StyledTableCell
                    key={column.id}
                    align={column.align}
                    style={{ minWidth: column.minWidth }}
                  >
                    <div style={{ whiteSpace: 'nowrap', padding: 5 }}>
                      {column.label}
                    </div>
                  </StyledTableCell>
                ))}
              </TableRow>
            </TableHead>
            {_.size(invoice) ? (
              <TableBody>
                {displayedData.map((item, index) => (
                  <StyledTableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={index}
                  >
                    {columns.map((column) => {
                      const value = item[column.id];

                      if (column.id === 'uploadRef') {
                        return (
                          <TableCell
                            sx={{ fontWeight: 'bold', fontFamily: 'monospace' }}
                          >
                            {uploadRef}
                          </TableCell>
                        );
                      }

                      if (column.id === 'transaction_ref') {
                        return (
                          <TableCell key={column.id} align={column.align}>
                            {transaction?.transaction_ref}
                          </TableCell>
                        );
                      }

                      if (column.id === 'invoice_no') {
                        return (
                          <TableCell
                            sx={{ color: 'primary.light', fontWeight: 'bold' }}
                            key={column.id}
                            align={column.align}
                          >
                            {value}
                          </TableCell>
                        );
                      }

                      if (column.id === 'amount') {
                        return (
                          <TableCell align={column.align}>
                            {addCommas(value)}
                          </TableCell>
                        );
                      }

                      if (column.id === 'pay_amount') {
                        return (
                          <TableCell align={column.align}>
                            {addCommas(value)}
                          </TableCell>
                        );
                      }

                      if (column.id === 'wht_amount') {
                        return (
                          <TableCell align={column.align}>
                            {addCommas(value)}
                          </TableCell>
                        );
                      }
                      if (column.id === 'payment_date') {
                        return (
                          <TableCell key={column.id} align={column.align}>
                            {dayjs(transaction.payment_date).format('YYYY-MM-DD • HH:mm:ss')}
                          </TableCell>
                        );
                      }

                      if (column.id === 'price_gl_diff') {
                        return (
                          <TableCell align={column.align}>
                            {addCommas(value)}
                          </TableCell>
                        );
                      }

                      if (column.id === 'fee') {
                        return (
                          <TableCell align={column.align}>
                            {transaction.total_fee
                              ? addCommas(transaction.total_fee)
                              : '0.00'}
                          </TableCell>
                        );
                      }
                      if (column.id === 'customer_code') {
                        const customerCode =
                          item.customer_code ||
                          item.sold_to_party ||
                          item.customer?.customer_code;
                        return (
                          <TableCell key={column.id} align={column.align}>
                            {customerCode || '-'}
                          </TableCell>
                        );
                      }

                      return (
                        <TableCell key={column.id} align={column.align}>
                          {value}
                        </TableCell>
                      );
                    })}
                  </StyledTableRow>
                ))}
              </TableBody>
            ) : (
              <TableBody>
                <TableRow>
                  <TableCell colSpan={15} align="center">
                    <div>ไม่มีข้อมูล</div>
                  </TableCell>
                </TableRow>
              </TableBody>
            )}
          </Table>
        </TableContainer>

        <TablePagination
          rowsPerPageOptions={[5, 25, 100]}
          component="div"
          count={dataFiltered ? dataFiltered.length : 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      {/* <Divider sx={{marginTop:'2%'}}/>
      
      
      
      <div>
        
        <FormControlLabel
          control={
            <Checkbox
              defaultChecked
              sx={{
                '&.Mui-checked': {
                  color: pink[600],
                },
              }}
            />
          }
          label="รูปภาพไม่คมชัด"
        />
      </div>
      <div>
        <FormControlLabel
          control={
            <Checkbox
              defaultChecked
              sx={{
                '&.Mui-checked': {
                  color: pink[600],
                },
              }}
            />
          }
          label="รูปภาพไม่ถูกต้อง"
        />
      </div>
      <div>
        <FormControlLabel
          control={
            <Checkbox
              defaultChecked
              sx={{
                '&.Mui-checked': {
                  color: pink[600],
                },
              }}
            />
          }
          label="จำนวนเงินที่ชำระไม่ตรงตามใบแจ้งหนี้"
        />
      </div>
      <div>
        <FormControlLabel
          control={
            <Checkbox
              defaultChecked
              sx={{
                '&.Mui-checked': {
                  color: pink[600],
                },
              }}
            />
          }
          label="อื่นๆ"
        />
      </div>
      
      <RejectedButton/> */}
    </div>
  );
}

export default TablePendingDetail;
